import React, { useEffect, useState, useRef } from 'react';
import './Header.css';
import { FaUser, FaShoppingCart, FaSearch, FaBars } from 'react-icons/fa';
import { getFirestore, collection, getDocs, doc, getDoc, orderBy, query } from 'firebase/firestore';
import { app } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';

const db = getFirestore(app);

type Category = {
    id: string;
    name: string;
    parentId: string;
    index?: number;
};

type CartItem = {
    productId: string;
    quantity: number;
};

type Profile = {
    id: string;
    name: string;
    order?: number;
};

const CART_LOCAL_KEY = 'cart';

const Header: React.FC = () => {
    const navigate = useNavigate();
    const [, setCategories] = useState<Category[]>([]);
    const categoriesRef = useRef<Category[]>([]);
    const { user } = useAuth();
    const [firstName, setFirstName] = useState<string | null>(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [cartCount, setCartCount] = useState(0);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [promotion, setPromotion] = useState<{ content: string; isVisible: boolean; byAppointmentOnly?: { content?: string } } | null>(null);
    const promotionRef = useRef<{ content: string; isVisible: boolean } | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{ left: number; top: number } | null>(null);
    const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [hoveredProfile, setHoveredProfile] = useState(false);
    const [profileDropdownPosition, setProfileDropdownPosition] = useState<{ left: number; top: number } | null>(null);
    const userMenuButtonRef = useRef<HTMLButtonElement>(null);
    const [userMenuPosition, setUserMenuPosition] = useState<{ top: number; left: number } | null>(null);

    const updateUserMenuPosition = () => {
        const btn = userMenuButtonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const menuWidth = 160;
        setUserMenuPosition({
            top: rect.bottom + 6,
            left: Math.max(8, rect.right - menuWidth),
        });
    };

    const toggleUserMenu = () => {
        if (userMenuOpen) {
            setUserMenuOpen(false);
            setUserMenuPosition(null);
        } else {
            updateUserMenuPosition();
            setUserMenuOpen(true);
        }
    };

    useEffect(() => {
        if (!userMenuOpen) return;
        const handleClose = () => {
            setUserMenuOpen(false);
            setUserMenuPosition(null);
        };
        window.addEventListener('scroll', handleClose, true);
        window.addEventListener('resize', handleClose);
        return () => {
            window.removeEventListener('scroll', handleClose, true);
            window.removeEventListener('resize', handleClose);
        };
    }, [userMenuOpen]);

    useEffect(() => {
        // 1. 先从 localStorage 读取
        const cached = localStorage.getItem('categories');
        const cacheTime = localStorage.getItem('categories_time');
        const now = Date.now();
        const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
        const maxAge = 5 * 60 * 1000; // 5分钟缓存

        if (cached && cacheAge < maxAge) {
            try {
                const cats = JSON.parse(cached);
                categoriesRef.current = cats;
                setCategories(cats);
            } catch { }
        }

        // 2. 再请求 Firestore
        const fetchCategories = async () => {
            const q = query(collection(db, 'categories'), orderBy('index'));
            const querySnapshot = await getDocs(q);
            const cats: Category[] = [];
            querySnapshot.forEach((doc) => {
                cats.push({ id: doc.id, ...doc.data() } as Category);
            });
            // 3. 比较字符串，有变化才更新
            const catsStr = JSON.stringify(cats.map(c => ({ id: c.id, name: c.name, parentId: c.parentId })));
            const refStr = JSON.stringify(categoriesRef.current.map(c => ({ id: c.id, name: c.name, parentId: c.parentId })));
            if (catsStr !== refStr) {
                categoriesRef.current = cats;
                setCategories(cats);
                localStorage.setItem('categories', JSON.stringify(cats));
                localStorage.setItem('categories_time', Date.now().toString());
            }
        };
        fetchCategories();

        // 清理函数
        return () => {
            if (hideTimeout) {
                clearTimeout(hideTimeout);
            }
        };
    }, [hideTimeout]);

    // 获取当前登录用户的firstName
    useEffect(() => {
        const fetchFirstName = async () => {
            if (user) {
                // 1. 先从 localStorage 读取
                const cached = localStorage.getItem(`user_${user.uid}`);
                const cacheTime = localStorage.getItem(`user_${user.uid}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 10 * 60 * 1000; // 10分钟缓存

                if (cached && cacheAge < maxAge) {
                    try {
                        const userData = JSON.parse(cached);
                        setFirstName(userData.firstName || null);
                    } catch { }
                }

                // 2. 再请求 Firestore
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    const firstName = userData.firstName || null;
                    setFirstName(firstName);

                    // 3. 缓存用户数据
                    localStorage.setItem(`user_${user.uid}`, JSON.stringify({
                        firstName: firstName,
                        lastName: userData.lastName,
                        email: userData.email
                    }));
                    localStorage.setItem(`user_${user.uid}_time`, Date.now().toString());
                }
            } else {
                setFirstName(null);
            }
        };
        fetchFirstName();
    }, [user]);

    // 一级分类
    const topCategories = categoriesRef.current.filter(cat => cat.parentId === "null");
    const sortedTopCategories = [...topCategories].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    const getSubCategories = (parentId: string) => {
        return categoriesRef.current
            .filter(cat => cat.parentId === parentId)
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    };

    const handleSignOut = async () => {
        await signOut(auth);
        setUserMenuOpen(false);
        setUserMenuPosition(null);
        navigate('/');
    };

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    const handleCategoryHover = (categoryId: string, event: React.MouseEvent) => {
        // 清除之前的隐藏定时器
        if (hideTimeout) {
            clearTimeout(hideTimeout);
            setHideTimeout(null);
        }

        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        setHoveredCategory(categoryId);
        setDropdownPosition({
            left: rect.left,
            top: rect.bottom + 5
        });
    };

    const handleCategoryLeave = () => {
        // 设置延迟隐藏，给用户时间移动到dropdown
        const timeout = setTimeout(() => {
            setHoveredCategory(null);
            setDropdownPosition(null);
        }, 150); // 150ms延迟
        setHideTimeout(timeout);
    };

    const handleDropdownEnter = () => {
        // 当鼠标进入dropdown时，清除隐藏定时器
        if (hideTimeout) {
            clearTimeout(hideTimeout);
            setHideTimeout(null);
        }
    };

    const handleDropdownLeave = () => {
        // 当鼠标离开dropdown时，立即隐藏
        setHoveredCategory(null);
        setDropdownPosition(null);
    };

    const handleProfileDropdownEnter = () => {
        if (hideTimeout) {
            clearTimeout(hideTimeout);
            setHideTimeout(null);
        }
    };

    const handleProfileDropdownLeave = () => {
        setHoveredProfile(false);
        setProfileDropdownPosition(null);
    };

    // 购物车同步逻辑
    useEffect(() => {
        const fetchCart = async () => {
            if (user) {
                // 1. 先从 localStorage 读取
                const cached = localStorage.getItem(`cart_${user.uid}`);
                const cacheTime = localStorage.getItem(`cart_${user.uid}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 1 * 60 * 1000; // 1分钟缓存

                if (cached && cacheAge < maxAge) {
                    try {
                        const cart = JSON.parse(cached);
                        setCartCount(cart.reduce((sum: number, item: CartItem) => sum + item.quantity, 0));
                    } catch { }
                }

                // 2. 再请求 Firestore
                const cartDoc = await getDoc(doc(db, "carts", user.uid));
                let cart: CartItem[] = [];
                if (cartDoc.exists()) {
                    cart = cartDoc.data().items || [];
                }
                setCartCount(cart.reduce((sum, item) => sum + item.quantity, 0));

                // 3. 缓存购物车数据
                localStorage.setItem(`cart_${user.uid}`, JSON.stringify(cart));
                localStorage.setItem(`cart_${user.uid}_time`, Date.now().toString());
            } else {
                const localCart = localStorage.getItem(CART_LOCAL_KEY);
                let cart: CartItem[] = [];
                if (localCart) {
                    cart = JSON.parse(localCart);
                }
                setCartCount(cart.reduce((sum, item) => sum + item.quantity, 0));
            }
        };
        fetchCart();
        window.addEventListener('cart-updated', fetchCart);
        return () => window.removeEventListener('cart-updated', fetchCart);
    }, [user]);

    // 获取promotion信息
    useEffect(() => {
        // 1. 先从 localStorage 读取
        const cached = localStorage.getItem('promotion');
        const cacheTime = localStorage.getItem('promotion_time');
        const now = Date.now();
        const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
        const maxAge = 10 * 60 * 1000; // 10分钟缓存

        if (cached && cacheAge < maxAge) {
            try {
                const promo = JSON.parse(cached);
                promotionRef.current = promo;
                setPromotion(promo);
            } catch { }
        }

        // 2. 再请求 Firestore
        const fetchPromotion = async () => {
            try {
                const promotionDoc = await getDoc(doc(db, "promotions", "main"));
                if (promotionDoc.exists()) {
                    const data = promotionDoc.data();
                    const promo = {
                        content: data.content || '',
                        isVisible: data.isVisible || false,
                        byAppointmentOnly: data.byAppointmentOnly || {}
                    };
                    // 3. 比较字符串，有变化才更新
                    const promoStr = JSON.stringify(promo);
                    const refStr = JSON.stringify(promotionRef.current);
                    if (promoStr !== refStr) {
                        promotionRef.current = promo;
                        setPromotion(promo);
                        localStorage.setItem('promotion', JSON.stringify(promo));
                        localStorage.setItem('promotion_time', Date.now().toString());
                    }
                }
            } catch (error) {
                console.error('Failed to fetch promotion:', error);
            }
        };
        fetchPromotion();
    }, []);

    // 获取 profiles 列表（用于 Profile 下拉菜单）- 带缓存减少 Firestore 读取
    useEffect(() => {
        const cached = localStorage.getItem('profiles');
        const cacheTime = localStorage.getItem('profiles_time');
        const now = Date.now();
        const maxAge = 10 * 60 * 1000; // 10 分钟缓存
        if (cached && cacheTime && now - parseInt(cacheTime) < maxAge) {
            try {
                setProfiles(JSON.parse(cached));
                return;
            } catch { }
        }
        const fetchProfiles = async () => {
            try {
                const q = query(collection(db, 'profiles'), orderBy('order'));
                const snapshot = await getDocs(q);
                const list: Profile[] = [];
                snapshot.forEach((d) => {
                    const data = d.data();
                    list.push({ id: d.id, name: data.name || '', order: data.order });
                });
                setProfiles(list);
                localStorage.setItem('profiles', JSON.stringify(list));
                localStorage.setItem('profiles_time', Date.now().toString());
            } catch (error) {
                console.error('Failed to fetch profiles:', error);
            }
        };
        fetchProfiles();
    }, []);

    return (
        <header className="header">
            {/* Promotion Banner */}
            {promotion && promotion.isVisible && (
                <div style={{
                    width: '100%',
                    background: '#fff',
                    color: '#222',
                    textAlign: 'center',
                    padding: '8px 0',
                    fontSize: '18px',
                    fontWeight: '500',
                    letterSpacing: '0.5px',
                }}>
                    {promotion.content}
                </div>
            )}
            <div className="header-top">
                <div className="header-contact">
                    <div className="contact-item">
                        <strong>PHONE</strong>
                        <a href="tel:+14162978088" style={{ color: 'inherit', textDecoration: 'none' }}>
                            +1 (416) 297-8088
                        </a>
                    </div>
                    <div className="contact-item">
                        <strong>EMAIL</strong>
                        <a href="mailto:gracioushome.ca@gmail.com" style={{ color: 'inherit', textDecoration: 'none' }}>
                            gracioushome.ca@gmail.com
                        </a>
                    </div>
                    <div className="contact-item">
                        <strong>LOCATION</strong>
                        <a
                            href="https://www.google.com/maps/search/?api=1&query=41+Passmore+Ave+Unit+1,+Scarborough,+ON+M1V+4T1"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                            41 Passmore Ave Unit 1, Scarborough, ON M1V 4T1
                        </a>
                    </div>
                </div>
                <div className="header-main">
                    <img
                        src="/logo.png"
                        alt="Company Logo"
                        className="header-logo"
                        onClick={() => navigate('/')}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
                <div className="header-actions">
                    <FaSearch
                        size={20}
                        style={{ cursor: 'pointer', marginRight: 16, color: '#222', alignSelf: 'center' }}
                        onClick={() => setSearchOpen(true)}
                    />
                    {user && firstName ? (
                        <>
                            <span className="user-greeting">
                                Hi, {firstName}
                            </span>
                            <div className="user-menu-container">
                                <button
                                    ref={userMenuButtonRef}
                                    className="icon-btn"
                                    aria-label="User Menu"
                                    aria-expanded={userMenuOpen}
                                    onClick={toggleUserMenu}
                                >
                                    <FaUser size={20} />
                                </button>
                            </div>
                        </>
                    ) : (
                        <button
                            className="icon-btn"
                            aria-label="Login/Register"
                            onClick={() => navigate('/login')}
                        >
                            <FaUser size={20} />
                        </button>
                    )}
                    <button
                        className="icon-btn"
                        aria-label="Cart"
                        onClick={() => navigate('/cart')}
                    >
                        <FaShoppingCart size={20} />
                        <span className="cart-count">{cartCount}</span>
                    </button>
                    <button
                        className="mobile-menu-btn"
                        aria-label="Mobile Menu"
                        onClick={() => setMobileMenuOpen(v => !v)}
                    >
                        <FaBars size={20} />
                    </button>
                </div>
            </div>
            <nav className="header-nav">
                <div className="nav-container">
                    <ul className="nav-list">
                        <li><a href="/">Home</a></li>
                        {categoriesRef.current.length > 0
                            ? sortedTopCategories.map(cat => {
                                const hasSubCategories = getSubCategories(cat.id).length > 0;
                                if (!hasSubCategories) {
                                    return (
                                        <li key={cat.id}>
                                            <a href={`/category/${cat.id}`}>{cat.name}</a>
                                        </li>
                                    );
                                }
                                return (
                                    <li
                                        key={cat.id}
                                        className="category-menu"
                                        onMouseEnter={(e) => handleCategoryHover(cat.id, e)}
                                        onMouseLeave={handleCategoryLeave}
                                    >
                                        <a href={`/category/${cat.id}`}>{cat.name}</a>
                                    </li>
                                );
                            })
                            : <li style={{ opacity: 0.6 }}>Loading...</li>
                        }
                        {profiles.length > 0 && (
                            <li
                                className="category-menu"
                                onMouseEnter={(e) => {
                                    if (hideTimeout) {
                                        clearTimeout(hideTimeout);
                                        setHideTimeout(null);
                                    }
                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                    setHoveredProfile(true);
                                    setProfileDropdownPosition({ left: rect.left, top: rect.bottom + 5 });
                                }}
                                onMouseLeave={() => {
                                    const t = setTimeout(() => {
                                        setHoveredProfile(false);
                                        setProfileDropdownPosition(null);
                                    }, 150);
                                    setHideTimeout(t);
                                }}
                            >
                                <span style={{ color: '#222', fontWeight: 500, fontSize: '1.1rem', cursor: 'default' }}>Portfolio</span>
                            </li>
                        )}
                        <li><a href="/about">About Us</a></li>
                    </ul>
                </div>
            </nav>

            {/* 移动端菜单 */}
            {mobileMenuOpen && (
                <div className="mobile-menu">
                    <div className="mobile-menu-content">
                        <div className="mobile-menu-header">
                            <h3>Menu</h3>
                            <button
                                className="mobile-menu-close"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                ×
                            </button>
                        </div>
                        <ul className="mobile-nav-list">
                            <li><a href="/" onClick={() => setMobileMenuOpen(false)}>Home</a></li>
                            {categoriesRef.current.length > 0
                                ? sortedTopCategories.map(cat => (
                                    <li key={cat.id}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <a
                                                href={`/category/${cat.id}`}
                                                onClick={() => setMobileMenuOpen(false)}
                                                style={{ flex: 1 }}
                                            >
                                                {cat.name}
                                            </a>
                                            {getSubCategories(cat.id).length > 0 && (
                                                <button
                                                    onClick={() => toggleCategory(cat.id)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        fontSize: '18px',
                                                        cursor: 'pointer',
                                                        padding: '8px',
                                                        color: '#666',
                                                        marginLeft: '10px'
                                                    }}
                                                >
                                                    {expandedCategories.has(cat.id) ? '−' : '+'}
                                                </button>
                                            )}
                                        </div>
                                        {getSubCategories(cat.id).length > 0 && expandedCategories.has(cat.id) && (
                                            <ul className="mobile-submenu">
                                                {getSubCategories(cat.id).map(sub => (
                                                    <li key={sub.id}>
                                                        <a href={`/category/${sub.id}`} onClick={() => setMobileMenuOpen(false)}>
                                                            {sub.name}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))
                                : <li style={{ opacity: 0.6 }}>Loading...</li>
                            }
                            {profiles.length > 0 && (
                                <li>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ flex: 1, fontWeight: 500 }}>Profile</span>
                                        <button
                                            onClick={() => toggleCategory('__profile__')}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '18px',
                                                cursor: 'pointer',
                                                padding: '8px',
                                                color: '#666',
                                                marginLeft: '10px'
                                            }}
                                        >
                                            {expandedCategories.has('__profile__') ? '−' : '+'}
                                        </button>
                                    </div>
                                    {expandedCategories.has('__profile__') && (
                                        <ul className="mobile-submenu">
                                            {profiles.map((p) => (
                                                <li key={p.id}>
                                                    <a href={`/gallery/${p.id}`} onClick={() => setMobileMenuOpen(false)}>
                                                        {p.name}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            )}
                            <li><a href="/about" onClick={() => setMobileMenuOpen(false)}>About Us</a></li>
                        </ul>
                    </div>
                </div>
            )}
            {/* 悬浮搜索框 */}
            {searchOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.25)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => setSearchOpen(false)}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 12,
                            padding: window.innerWidth <= 768 ? '20px' : '32px',
                            minWidth: window.innerWidth <= 768 ? '280px' : '350px',
                            maxWidth: window.innerWidth <= 768 ? '90vw' : '480px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                            position: 'relative'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <form
                            onSubmit={e => {
                                e.preventDefault();
                                if (searchText.trim()) {
                                    setSearchOpen(false);
                                    navigate(`/search?keyword=${encodeURIComponent(searchText.trim())}`);
                                    setSearchText('');
                                }
                            }}
                            style={{ width: '100%' }} // 保证表单宽度100%
                        >
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search products with keywords..."
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: window.innerWidth <= 768 ? 16 : 18,
                                    borderRadius: 6,
                                    border: '1px solid #ccc',
                                    marginBottom: 12,
                                    boxSizing: 'border-box'
                                }}
                            />
                            <button
                                type="submit"
                                style={{
                                    width: '100%',
                                    background: '#27ae60',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: '10px 0',
                                    fontWeight: 600,
                                    fontSize: window.innerWidth <= 768 ? 16 : 18,
                                    cursor: 'pointer'
                                }}
                            >
                                Search
                            </button>
                        </form>
                        <button
                            onClick={() => setSearchOpen(false)}
                            style={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                background: 'none',
                                border: 'none',
                                fontSize: 22, // 调大关闭按钮
                                cursor: 'pointer'
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* 用户菜单 - fixed 定位，避免被 header 裁剪 */}
            {userMenuOpen && userMenuPosition && (
                <ul
                    className="user-menu-dropdown user-menu-dropdown-fixed"
                    style={{
                        top: userMenuPosition.top,
                        left: userMenuPosition.left,
                    }}
                    onMouseLeave={() => {
                        setUserMenuOpen(false);
                        setUserMenuPosition(null);
                    }}
                >
                    <li>
                        <button
                            type="button"
                            onClick={() => {
                                setUserMenuOpen(false);
                                setUserMenuPosition(null);
                                navigate('/profile');
                            }}
                        >
                            Profile Info
                        </button>
                    </li>
                    <li>
                        <button
                            type="button"
                            onClick={() => {
                                setUserMenuOpen(false);
                                setUserMenuPosition(null);
                                navigate('/orders');
                            }}
                        >
                            Order Info
                        </button>
                    </li>
                    <li>
                        <button type="button" onClick={handleSignOut}>
                            Sign Out
                        </button>
                    </li>
                </ul>
            )}

            {/* Global Dropdown */}
            {hoveredCategory &&
                dropdownPosition &&
                getSubCategories(hoveredCategory).length > 0 && (
                <div
                    className="dropdown"
                    style={{
                        position: 'fixed',
                        left: dropdownPosition.left,
                        top: dropdownPosition.top,
                        zIndex: 10000,
                        background: '#fff',
                        minWidth: 160,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: 4,
                        border: '1px solid #e0e0e0',
                        padding: 0,
                        margin: 0
                    }}
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                >
                    {getSubCategories(hoveredCategory).map(sub => (
                        <div key={sub.id} style={{ padding: 0, margin: 0 }}>
                            <a
                                href={`/category/${sub.id}`}
                                style={{
                                    display: 'block',
                                    padding: '8px 16px',
                                    color: '#222',
                                    textDecoration: 'none',
                                    fontSize: '14px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                {sub.name}
                            </a>
                        </div>
                    ))}
                </div>
            )}

            {/* Profile 下拉菜单 */}
            {hoveredProfile && profileDropdownPosition && profiles.length > 0 && (
                <div
                    className="dropdown"
                    style={{
                        position: 'fixed',
                        left: profileDropdownPosition.left,
                        top: profileDropdownPosition.top,
                        zIndex: 10000,
                        background: '#fff',
                        minWidth: 160,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: 4,
                        border: '1px solid #e0e0e0',
                        padding: 0,
                        margin: 0
                    }}
                    onMouseEnter={handleProfileDropdownEnter}
                    onMouseLeave={handleProfileDropdownLeave}
                >
                    {profiles.map((p) => (
                        <div key={p.id} style={{ padding: 0, margin: 0 }}>
                            <a
                                href={`/gallery/${p.id}`}
                                style={{
                                    display: 'block',
                                    padding: '8px 16px',
                                    color: '#222',
                                    textDecoration: 'none',
                                    fontSize: '14px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                {p.name}
                            </a>
                        </div>
                    ))}
                </div>
            )}
        </header>
    );
};

export default Header;